/**
 * @module UseNotifications
 * @package @geenius/notifications-react-css
 * @description Implements the primary inbox hook for the React vanilla CSS
 * notifications variant.
 */

import type {
  Notification,
  NotificationStatus,
  NotificationType,
} from '@geenius/notifications/shared'
import { groupByDate } from '@geenius/notifications/shared'
import { useMemo, useState } from 'react'

/**
 * Async handlers supported by the inbox hook.
 */
export interface NotificationActionHandlers {
  markRead: (id: string) => Promise<void>
  markAllRead: () => Promise<void>
  dismiss: (id: string) => Promise<void>
  deleteNotification: (id: string) => Promise<void>
}

/**
 * Shape returned by {@link useNotifications}.
 */
export interface UseNotificationsResult {
  notifications: Notification[]
  allNotifications: Notification[]
  unreadCount: number
  grouped: Map<string, Notification[]>
  statusFilter: NotificationStatus | 'all'
  setStatusFilter: (status: NotificationStatus | 'all') => void
  typeFilter: NotificationType | 'all'
  setTypeFilter: (type: NotificationType | 'all') => void
  isLoading: boolean
  markRead?: NotificationActionHandlers['markRead']
  markAllRead?: NotificationActionHandlers['markAllRead']
  dismiss?: NotificationActionHandlers['dismiss']
  deleteNotification?: NotificationActionHandlers['deleteNotification']
}

/**
 * Computes derived inbox state for the React CSS notification views.
 *
 * @param notifications Source notification list, or `undefined` while loading.
 * @param actions Optional async mutation handlers for notification actions.
 * @returns Derived inbox state and mutation callbacks.
 */
export function useNotifications(
  notifications: Notification[] | undefined,
  actions?: Partial<NotificationActionHandlers>,
): UseNotificationsResult {
  const [statusFilter, setStatusFilter] = useState<NotificationStatus | 'all'>('all')
  const [typeFilter, setTypeFilter] = useState<NotificationType | 'all'>('all')
  const isLoading = notifications === undefined

  const filtered = useMemo(() => {
    let nextNotifications = notifications ?? []

    if (statusFilter !== 'all') {
      nextNotifications = nextNotifications.filter(
        (notification) => notification.status === statusFilter,
      )
    }

    if (typeFilter !== 'all') {
      nextNotifications = nextNotifications.filter(
        (notification) => notification.type === typeFilter,
      )
    }

    return nextNotifications
  }, [notifications, statusFilter, typeFilter])

  const unreadCount = useMemo(
    () => (notifications ?? []).filter((notification) => notification.status === 'unread').length,
    [notifications],
  )
  const grouped = useMemo(() => groupByDate(filtered), [filtered])

  return {
    notifications: filtered,
    allNotifications: notifications ?? [],
    unreadCount,
    grouped,
    statusFilter,
    setStatusFilter,
    typeFilter,
    setTypeFilter,
    isLoading,
    markRead: actions?.markRead,
    markAllRead: actions?.markAllRead,
    dismiss: actions?.dismiss,
    deleteNotification: actions?.deleteNotification,
  }
}
